<vs-input-container
  #trigger="cdkOverlayOrigin"
  cdkOverlayOrigin
  class="cursor-pointer"
  (click)="$event.preventDefault(); $event.stopImmediatePropagation(); toggleOverlay()"
>
  <div *vsLet="selectedNames$ | async as names" class="flex items-center w-full min-w-0">
    <span *ngIf="names" class="text-sm text-gray-900 truncate">{{ names }}</span>
    <span *ngIf="!names" class="text-sm text-gray-400">
      {{ (bundles$ | async)?.length ? placeholder : 'No bundles available' }}
    </span>
  </div>

  <vs-icon slot="end" name="chevron-down" size="20" class="text-gray-500" [class.rotate-180]="overlayOpen"></vs-icon>
</vs-input-container>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOpen]="overlayOpen"
  [cdkConnectedOverlayOrigin]="trigger"
  (overlayOutsideClick)="close()"
>
  <vs-card
    class="my-2 !rounded-md !shadow-md"
    [style.width]="elementRef.nativeElement.offsetWidth + 'px'"
    [style.min-width.px]="260"
  >
    <vs-input-container class="!border-x-0 border-t-0 !rounded-b-none">
      <vs-icon slot="start" name="search-md"></vs-icon>

      <input
        #searchInput
        type="text"
        placeholder="Search bundles"
        (keydown.enter)="$event.preventDefault()"
        (input)="searchQuery$.next($any($event.target).value)"
      />
    </vs-input-container>

    <div *vsLet="filteredBundles$ | async as filtered" class="px-2 py-2 max-h-60 overflow-auto scrollbar-thin">
      <p *ngIf="!filtered?.length" class="px-2 py-3 text-sm text-gray-500">
        {{ searchQuery$.value ? 'No bundles match your search.' : 'No active bundles.' }}
      </p>

      <div
        *ngFor="let bundle of filtered"
        class="flex items-center gap-2 px-2 py-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-all"
        tooltipStyle="light"
        tooltipPosition="end"
        [vsTooltip]="{ template: rolesTooltip, context: { $implicit: bundle } }"
        (click)="toggle(bundle.id)"
      >
        <input vsInput type="checkbox" class="self-center shrink-0" [checked]="isSelected(bundle.id)" />

        <span class="text-sm font-medium text-gray-900 truncate">{{ bundle.name }}</span>
        <span class="text-xs text-gray-500 whitespace-nowrap ml-auto">{{ definedCountLabel(bundle) }}</span>
      </div>
    </div>
  </vs-card>
</ng-template>

<!-- Hover: a bundle's roles, before it is picked. -->
<ng-template #rolesTooltip let-bundle>
  <div class="p-1 max-w-[240px]">
    <p class="text-xs font-semibold text-gray-700 mb-1">{{ bundle.name }} grants:</p>

    <div *ngIf="bundle.items?.length; else noRoles" class="flex flex-wrap gap-1">
      <vs-badge *ngFor="let item of bundle.items" [theme]="roleTheme(item.roleKey)">
        {{ roleTitle(item.roleKey) }}
      </vs-badge>
    </div>

    <ng-template #noRoles>
      <p class="text-xs text-gray-600">No roles in this bundle.</p>
    </ng-template>
  </div>
</ng-template>

<div *vsLet="selectedBundles$ | async as selected" class="flex flex-col gap-2" [class.mt-3]="selected?.length">
  <div *ngFor="let bundle of selected" class="rounded-lg border border-gray-200 bg-gray-50 p-3">
    <div class="flex items-start justify-between gap-2">
      <div class="flex items-center gap-2 min-w-0">
        <vs-icon name="layers-three-01" size="16" class="text-gray-500 shrink-0"></vs-icon>
        <span class="text-sm font-semibold text-gray-900 truncate">{{ bundle.name }}</span>
        <span class="text-xs text-gray-500 shrink-0">{{ roleCountLabel(bundle) }}</span>

        <!-- Deactivated after assignment: still grants its roles until removed here. -->
        <vs-badge *ngIf="!bundle.isActive" theme="warn" class="shrink-0">Deactivated</vs-badge>

        <!-- Edited since this member got it: they keep the roles below until the bundle is applied to them. -->
        <vs-badge
          *ngIf="isOutOfDate(bundle)"
          theme="info"
          class="shrink-0"
          vsTooltip="This bundle has changed. Apply it from the Role Bundles tab to update members."
        >
          Not applied
        </vs-badge>
      </div>

      <button
        *ngIf="!disabled"
        type="button"
        class="shrink-0 text-gray-400 hover:text-gray-700"
        vsTooltip="Remove bundle"
        (click)="remove(bundle.id)"
      >
        <vs-icon name="x-close" size="16"></vs-icon>
      </button>
    </div>

    <!-- The roles the member actually holds from this bundle, not the bundle's current definition.
         Same colours as the roles picker below; the card is what marks them as bundle-granted. -->
    <div class="flex flex-wrap gap-1 mt-2">
      <vs-badge *ngFor="let roleKey of visibleRoles(bundle)" [theme]="roleTheme(roleKey)">
        {{ roleTitle(roleKey) }}
      </vs-badge>

      <vs-badge
        *ngIf="hiddenRoleCount(bundle)"
        theme="default"
        tooltipStyle="light"
        [vsTooltip]="{ template: hiddenRoles, context: { $implicit: bundle } }"
      >
        +{{ hiddenRoleCount(bundle) }} more
      </vs-badge>

      <span *ngIf="!roleKeysOn(bundle).length" class="text-xs text-gray-500">No roles from this bundle.</span>
    </div>
  </div>
</div>

<ng-template #hiddenRoles let-bundle>
  <div class="flex flex-wrap gap-1 p-1 max-w-[220px]">
    <vs-badge *ngFor="let roleKey of hiddenRolesOf(bundle)" [theme]="roleTheme(roleKey)">
      {{ roleTitle(roleKey) }}
    </vs-badge>
  </div>
</ng-template>
